<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  auth,
  signInWithEmailAndPassword,
  db,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  functions,
  httpsCallable,
} from "@/firebase";
import FileReport from "@/components/FileReport.vue";
import { trustedDeviceService } from "@/services/trustedDeviceService";
import { twoFactorAuthService } from "@/services/twoFactorAuthService";
import { logDebug, logInfo, logError, logSuccess, logWarn } from "@/utils/secureLogger";
import { createAuditLog } from "@/services/auditLogService";

// Check if current device is trusted on mount
onMounted(async () => {
  document.title = "ElevatED – Login";

  // Preload login background image
  const preloadLink = document.createElement('link');
  preloadLink.rel = 'preload';
  preloadLink.as = 'image';
  preloadLink.href = '/login.webp';
  preloadLink.type = 'image/webp';
  document.head.appendChild(preloadLink);

  const storedStaySignedIn =
    localStorage.getItem("staySignedIn") ?? localStorage.getItem("rememberMe");
  staySignedIn.value = storedStaySignedIn === "true";
});

const checkTrustedDevice = async () => {
  try {
    // ALWAYS verify with server for security - don't trust cache alone
    // Cache is only for UI optimization, not security decisions
    const verification = await trustedDeviceService.verifyTrustedDevice();
    isTrustedDevice.value = verification.isTrusted;

    if (verification.isTrusted) {
      trustedDeviceService.setDeviceTrusted(true, verification.deviceName);
    } else {
      // Clear cache if device is not trusted
      localStorage.removeItem("deviceTrusted");
    }
  } catch (error) {
    // On any error, assume device is NOT trusted (fail secure)
    isTrustedDevice.value = false;
    localStorage.removeItem("deviceTrusted");

    // Only log error if it's not the "Only admins" error (which is expected for non-admins)
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes("Only admins")) {
      logDebug("Trusted device check skipped - admin-only feature");
    } else {
      logError("Error verifying trusted device", error);
    }
  }
};

const router = useRouter();
const email = ref("");
const password = ref("");
const errorMessage = ref("");
const showPassword = ref(false);
const staySignedIn = ref(false);
const showTrustDevice = ref(false);
const showEmailConfirmation = ref(false);
const deviceName = ref("");
const isTrustedDevice = ref(false);
const pendingAdminAccess = ref<{ email: string; userType: string; confirmationId: string } | null>(
  null
);
const emailConfirmationStatus = ref("pending"); // pending, confirmed, expired

// Loading state
const isLoading = ref(false);
const loadingMessage = ref("");

// Validation state
const emailError = ref("");
const passwordError = ref("");

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Real-time email validation
const validateEmail = () => {
  if (!email.value.trim()) {
    emailError.value = "";
    return false;
  }
  if (!emailRegex.test(email.value)) {
    emailError.value = "Please enter a valid email address";
    return false;
  }
  emailError.value = "";
  return true;
};

// Real-time password validation
const validatePassword = () => {
  if (!password.value.trim()) {
    passwordError.value = "";
    return false;
  }
  // Password validation removed - no minimum character requirement on login
  passwordError.value = "";
  return true;
};

// Rate limiting state
const failedAttempts = ref(0);
const lastAttemptTime = ref(0);
const isRateLimited = ref(false);
const remainingDelay = ref(0);

// Progressive delay calculation (1s, 5s, 10s, 30s, 60s, then 60s for subsequent attempts)
const calculateDelay = (attempts: number): number => {
  const delays = [0, 1000, 5000, 10000, 30000, 60000]; // milliseconds
  return delays[Math.min(attempts, delays.length - 1)];
};

// Check if rate limited and start countdown
const checkRateLimit = (): boolean => {
  const now = Date.now();
  const timeSinceLastAttempt = now - lastAttemptTime.value;
  const requiredDelay = calculateDelay(failedAttempts.value);

  if (timeSinceLastAttempt < requiredDelay) {
    isRateLimited.value = true;
    remainingDelay.value = Math.ceil((requiredDelay - timeSinceLastAttempt) / 1000);

    // Start countdown
    const countdown = setInterval(() => {
      remainingDelay.value--;
      if (remainingDelay.value <= 0) {
        isRateLimited.value = false;
        clearInterval(countdown);
      }
    }, 1000);

    return true;
  }

  return false;
};

// Reset rate limiting on successful login
const resetRateLimit = () => {
  failedAttempts.value = 0;
  lastAttemptTime.value = 0;
  isRateLimited.value = false;
  remainingDelay.value = 0;
};

// Record failed attempt
const recordFailedAttempt = () => {
  failedAttempts.value++;
  lastAttemptTime.value = Date.now();
};

// Check if user is verified in Firestore
const checkUserVerification = async (userId: string) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.isVerified === true;
    }
    return false;
  } catch (error) {
    logError("Error checking user verification", error);
    return false;
  }
};

const handleEmailLogin = async () => {
  errorMessage.value = "";
  emailError.value = "";
  passwordError.value = "";
  isLoading.value = true;
  loadingMessage.value = "Signing you in...";

  // Validate email and password
  const isEmailValid = validateEmail();
  const isPasswordValid = validatePassword();

  if (!isEmailValid || !isPasswordValid) {
    isLoading.value = false;
    loadingMessage.value = "";
    return;
  }

  // Check if rate limited
  if (checkRateLimit()) {
    errorMessage.value = `Too many failed attempts. Please wait before trying again.`;
    isLoading.value = false;
    loadingMessage.value = "";
    return;
  }

  try {
    loadingMessage.value = "Authenticating with Firebase...";
    const userCredential = await signInWithEmailAndPassword(auth, email.value, password.value);
    const user = userCredential.user;

    loadingMessage.value = "Verifying your account...";

    // Get user type from Firestore first
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);

    // Check if user document exists - if not, account is deactivated/archived
    if (!userDoc.exists()) {
      errorMessage.value =
        "This account has been deactivated. Please contact support if you believe this is an error.";
      await auth.signOut(); // Sign out the user since their account is deactivated
      isLoading.value = false;
      loadingMessage.value = "";
      return;
    }

    const userData = userDoc.data();
    const userType = userData?.userType || "";

    const isAdmin = userType === "admin" || userType === "superadmin";

    if (!isAdmin) {
      let isVerified = await checkUserVerification(user.uid);

      // If not verified in Firestore, check Firebase Auth and update if needed
      if (!isVerified) {
        logDebug("User not verified in Firestore, checking Firebase Auth...");
        await user.reload(); // Reload to get latest verification status

        if (user.emailVerified) {
          logDebug("User is verified in Firebase Auth, updating Firestore...");
          // Update Firestore to match Firebase Auth verification status
          try {
            await updateDoc(doc(db, "users", user.uid), {
              isEmailVerified: true,
              isVerified: true,
              emailVerifiedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            logSuccess("Firestore updated with verification status");
            isVerified = true;
          } catch (error) {
            logError("Error updating Firestore verification status", error);
          }
        }
      }

      if (!isVerified) {
        errorMessage.value =
          "Please verify your email address before logging in. Check your inbox for the verification link.";
        await auth.signOut(); // Sign out the user since they're not verified
        isLoading.value = false;
        loadingMessage.value = "";
        return;
      }
    } else {
      logInfo("Admin user detected - skipping email verification, will use 2FA");
    }

    // Get a fresh Firebase ID token (force refresh to avoid stale/expired token)
    loadingMessage.value = "Getting authentication token...";
    const idToken = await user.getIdToken(true);

    // Send token to Django backend
    loadingMessage.value = "Connecting to server...";
    const API_BASE_URL =
      import.meta.env.VITE_API_BASE_URL || "https://elevated-backend-xyaskmbwha-uc.a.run.app/api";
    const response = await fetch(`${API_BASE_URL}/firebase/login/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firebase_token: idToken,
        remember_me: staySignedIn.value,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      logSuccess("Login successful", { userType: data.user.user_type });
      logDebug("Full user data received", data.user);

      // Reset rate limiting on successful login
      resetRateLimit();

      loadingMessage.value = "Setting up your session...";

      // After successful login, refresh trusted device status only for admins
      // This must come BEFORE any 2FA or trusted device checks
      const isAdmin = data.user.user_type === "admin" || data.user.user_type === "superadmin";
      if (isAdmin) {
        await checkTrustedDevice();
      } else {
        // For non-admin users, always set as not requiring trusted device checks
        isTrustedDevice.value = true; // Skip 2FA for non-admin users
      }

      // Handle remember me functionality
      if (staySignedIn.value) {
        // Store token with longer expiration or persistent storage
        localStorage.setItem("token", data.access);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("staySignedIn", "true");
      } else {
        // Use sessionStorage for temporary storage (cleared when browser closes)
        sessionStorage.setItem("token", data.access);
        sessionStorage.setItem("user", JSON.stringify(data.user));
        sessionStorage.setItem("rememberMe", "false");
        sessionStorage.setItem("staySignedIn", "false");
      }

      // Redirect based on user type and application status from Firestore
      logDebug("About to redirect", { userType: data.user.user_type });

      // Check if user is admin or superadmin
      if (data.user.user_type === "admin" || data.user.user_type === "superadmin") {
        logInfo("Admin login successful");

        // Only require 2FA if device is not already trusted
        if (!isTrustedDevice.value) {
          logDebug("Device not trusted, requiring 2FA");

          loadingMessage.value = "Sending verification code...";

          try {
            // Send 2FA code via email
            const twoFAResult = await twoFactorAuthService.send2FACode(
              data.user.email,
              data.user.user_type
            );

            if (twoFAResult.success) {
              logSuccess("2FA code sent successfully");
              isLoading.value = false;
              loadingMessage.value = "";

              // Redirect to 2FA verification page
              router.push({
                name: "TwoFactor",
                params: { twoFAId: twoFAResult.twoFAId },
                query: { expiresAt: twoFAResult.expiresAt },
              });
              return; // Don't redirect yet, wait for 2FA verification
            } else {
              errorMessage.value = "Failed to send verification code. Please try again.";
              isLoading.value = false;
              loadingMessage.value = "";
              return;
            }
          } catch (twoFAError: any) {
            logError("Error sending 2FA code", twoFAError);
            errorMessage.value = twoFAError.message || "Failed to send verification code";
            isLoading.value = false;
            loadingMessage.value = "";
            return;
          }
        } else {
          logDebug("Device already trusted, redirecting directly");
          loadingMessage.value = "Redirecting to admin dashboard...";

          // Create audit log for admin login
          try {
            const { getCurrentUserFullName } = await import("@/utils/userUtils");
            const performedByName = await getCurrentUserFullName();

            await createAuditLog({
              action: "admin_auth",
              type: "system_event",
              details: `Admin logged in successfully from trusted device`,
              performedById: user.uid,
              performedByName: performedByName,
              performedByEmail: data.user.email || "",
              affectedUserId: user.uid,
              affectedUserName: performedByName,
              affectedUserEmail: data.user.email || "",
            });
          } catch (auditError) {
            console.error("Failed to create audit log for admin login:", auditError);
          }

          // Device is already trusted, redirect directly
          router.push("/admin");
          return;
        }
      } else if (data.user.user_type === "validator") {
        // Validators go to AI Testing page
        loadingMessage.value = "Redirecting to AI Testing...";
        router.push("/validator/ai-testing");
      } else if (data.user.user_type === "tutor") {
        logDebug("Redirecting tutor based on application status");
        loadingMessage.value = "Checking your tutor status...";
        // Check tutor application status from Firestore
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const applicationStatus = userData.applicationStatus || "pending";
            const profileStatus = userData.profileStatus || "pending_approval";

            if (applicationStatus === "approved" && profileStatus === "approved") {
              loadingMessage.value = "Redirecting to tutor dashboard...";
              // Approved tutor, go to dashboard
              router.push("/tutor/sessions");
            } else {
              loadingMessage.value = "Redirecting to application status...";
              // Tutor pending approval, go to application status
              router.push("/tutor/application-status");
            }
          } else {
            loadingMessage.value = "Redirecting to application status...";
            // No user document found, go to application status
            router.push("/tutor/application-status");
          }
        } catch (error) {
          logError("Error checking tutor status from Firestore", error);
          loadingMessage.value = "Redirecting to application status...";
          router.push("/tutor/application-status");
        }
      } else {
        logDebug("Redirecting student based on application status");
        loadingMessage.value = "Checking your student status...";
        // Check student application status from Firestore
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));

          if (userDoc.exists()) {
            const userData = userDoc.data();
            const applicationStatus = userData.applicationStatus || "pending";

            if (applicationStatus === "rejected") {
              loadingMessage.value = "Redirecting to rejection page...";
              // Rejected student, go to rejection page
              router.push("/student/rejection");
            } else if (
              applicationStatus === "approved" ||
              applicationStatus === "active" ||
              applicationStatus === "Active"
            ) {
              loadingMessage.value = "Redirecting to Find Tutors...";
              // Approved student, go to Find Tutors page
              router.push("/student/find-tutors");
            } else {
              loadingMessage.value = "Redirecting to application status...";
              // Student pending approval, go to application status
              router.push("/student/application-status");
            }
          } else {
            loadingMessage.value = "Redirecting to application status...";
            // No user document found, go to application status
            router.push("/student/application-status");
          }
        } catch (error) {
          logError("Error checking student status from Firestore", error);
          loadingMessage.value = "Redirecting to application status...";
          router.push("/student/application-status");
        }
      }
    } else {
      const errorData = await response.json();
      logError("Login failed", errorData);
      errorMessage.value = errorData.error || "Login failed";
      isLoading.value = false;
      loadingMessage.value = "";
    }
  } catch (error: any) {
    // Record failed attempt for rate limiting
    recordFailedAttempt();

    // Only log as warning for invalid credentials (expected user error)
    // Log as error for unexpected issues
    if (error.code === "auth/invalid-credential") {
      logWarn("Login attempt with invalid credentials");
      errorMessage.value = "The Email or Password you entered is incorrect.";
    } else {
      logError("Login error occurred", error);
      errorMessage.value = error.message || "An error occurred during login";
    }

    isLoading.value = false;
    loadingMessage.value = "";
  }
};

// Trust device functions for admins
const addTrustedDevice = async () => {
  try {
    const deviceInfo = trustedDeviceService.getDeviceInfo();
    const name = deviceName.value.trim() || deviceInfo.name;

    const result = await trustedDeviceService.addTrustedDevice(name);

    if (result.success) {
      // Update UI
      isTrustedDevice.value = true;
      showTrustDevice.value = false;

      // Create audit log for admin login
      try {
        const { getCurrentUserFullName } = await import("@/utils/userUtils");
        const performedByName = await getCurrentUserFullName();

        await createAuditLog({
          action: "admin_auth",
          type: "system_event",
          details: `Admin logged in successfully and added trusted device`,
          performedById: auth.currentUser?.uid,
          performedByName: performedByName,
          performedByEmail: auth.currentUser?.email || "",
          affectedUserId: auth.currentUser?.uid,
          affectedUserName: performedByName,
          affectedUserEmail: auth.currentUser?.email || "",
        });
      } catch (auditError) {
        console.error("Failed to create audit log for admin login:", auditError);
      }

      // Redirect to admin dashboard
      router.push("/admin");
    } else if (result.deviceLimitReached) {
      errorMessage.value =
        result.message ||
        "Maximum of 3 trusted devices allowed. Please remove an existing device first.";
      // Keep the modal open so user can see the error
    } else {
      errorMessage.value = result.message || "Failed to trust device";
    }
  } catch (error: any) {
    errorMessage.value = error.message || "Failed to add trusted device";
  }
};

const skipTrustDevice = async () => {
  showTrustDevice.value = false;

  // Create audit log for admin login
  try {
    const { getCurrentUserFullName } = await import("@/utils/userUtils");
    const performedByName = await getCurrentUserFullName();

    await createAuditLog({
      action: "admin_auth",
      type: "system_event",
      details: `Admin logged in successfully (skipped trusted device setup)`,
      performedById: auth.currentUser?.uid,
      performedByName: performedByName,
      performedByEmail: auth.currentUser?.email || "",
      affectedUserId: auth.currentUser?.uid,
      affectedUserName: performedByName,
      affectedUserEmail: auth.currentUser?.email || "",
    });
  } catch (auditError) {
    console.error("Failed to create audit log for admin login:", auditError);
  }

  router.push("/admin");
};

// Send email confirmation for untrusted device login
type UntrustedDeviceConfirmationResponse =
  | { success: true; confirmationId: string }
  | { success: false; error: string };

// Check email confirmation status
const checkEmailConfirmationStatus = async (confirmationId: string) => {
  try {
    const checkStatus = httpsCallable(functions, "checkEmailConfirmationStatus");
    const result = await checkStatus({ confirmationId });
    return result.data;
  } catch (error: any) {
    logError("Failed to check email confirmation status", error);
    return { status: "error", error: error.message };
  }
};

// Handle email confirmation success

// Handle email confirmation failure/timeout
const handleEmailConfirmationTimeout = () => {
  showEmailConfirmation.value = false;
  errorMessage.value = "Email confirmation timed out. Please try logging in again.";
};
</script>

<template>
  <FileReport />

  <div class="min-h-screen relative overflow-hidden">
    <!-- Background using login.webp with PNG fallback -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat login-bg"
    >
      <!-- Overlay to ensure text readability -->
      <div class="absolute inset-0 bg-blue-900/20"></div>
    </div>

    <!-- Decorative floating shapes -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <!-- Star shape top left -->
      <div class="absolute top-20 left-16 text-yellow-400 text-3xl animate-pulse">✦</div>
      <!-- Circle top right -->
      <div class="absolute top-32 right-20 w-4 h-4 bg-pink-400 rounded-full animate-bounce"></div>
      <!-- Plus bottom left -->
      <div class="absolute bottom-40 left-12 text-green-400 text-2xl animate-pulse">✚</div>
      <!-- Diamond right -->
      <div
        class="absolute top-1/2 right-16 w-6 h-6 bg-purple-400 transform rotate-45 animate-pulse"
      ></div>
      <!-- Circle bottom right -->
      <div
        class="absolute bottom-20 right-32 w-5 h-5 bg-orange-400 rounded-full animate-bounce"
      ></div>
      <!-- Clover left -->
      <div class="absolute top-1/3 left-20 text-green-500 text-2xl animate-pulse">♣</div>
      <!-- Star right -->
      <div class="absolute top-1/4 right-1/4 text-white text-xl animate-pulse">✦</div>
    </div>

    <!-- Content -->
    <div class="relative z-10 flex min-h-screen items-center justify-center px-6 py-32 lg:px-8">
      <div class="w-full max-w-md mt-15">
        <!-- Header -->
        <div class="text-center mb-8">
          <h1 class="text-5xl font-black text-white mb-4 tracking-wide">Log In to ElevatED</h1>
          <p class="text-xl text-white font-light italic tracking-wide">
            Continue your learning journey with us.
          </p>
        </div>

        <!-- Login Form Container -->
        <div
          class="login-container bg-white rounded-3xl shadow-2xl p-10 border-4 border-black relative"
        >
          <form class="space-y-5" @submit.prevent="handleEmailLogin">
            <!-- Email Field -->
            <div>
              <label for="email" class="block text-sm font-medium text-gray-600 mb-2">
                Email Address
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <input
                  v-model="email"
                  @input="validateEmail"
                  @blur="validateEmail"
                  type="email"
                  name="email"
                  id="email"
                  autocomplete="email"
                  required
                  placeholder="Enter your email"
                  :class="[
                    'block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-gray-50',
                    emailError ? 'border-red-500' : 'border-gray-300',
                  ]"
                />
              </div>
              <!-- Email Error Message -->
              <div v-if="emailError" class="mt-1 text-red-600 text-sm">
                {{ emailError }}
              </div>
            </div>

            <!-- Password Field -->
            <div>
              <label for="password" class="block text-sm font-medium text-gray-600 mb-2">
                Password
              </label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  v-model="password"
                  @input="validatePassword"
                  @blur="validatePassword"
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  id="password"
                  autocomplete="current-password"
                  required
                  placeholder="Enter your password"
                  maxlength="64"
                  :class="[
                    'block w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-gray-50',
                    passwordError ? 'border-red-500' : 'border-gray-300',
                  ]"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                >
                  <!-- Eye Slash Icon (when password is visible - click to hide) -->
                  <svg
                    v-if="showPassword"
                    class="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  <!-- Eye Icon (when password is hidden - click to show) -->
                  <svg
                    v-else
                    class="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
              </div>
              <!-- Password Error Message -->
              <div v-if="passwordError" class="mt-1 text-red-600 text-sm">
                {{ passwordError }}
              </div>
            </div>

            <!-- Remember Me and Forgot Password -->
            <div class="flex items-center justify-between pt-2">
              <div class="flex items-center">
                <input
                  id="stay-signed-in"
                  name="stay-signed-in"
                  type="checkbox"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                  v-model="staySignedIn"
                />
                <label for="stay-signed-in" class="ml-2 block text-sm text-gray-600 cursor-pointer">
                  Stay signed in
                </label>
              </div>
              <div class="text-sm">
                <router-link
                  to="/password-reset-request"
                  class="font-medium text-blue-600 hover:text-blue-500"
                >
                  Forgot password?
                </router-link>
              </div>
            </div>

            <!-- Error Message -->
            <div
              v-if="errorMessage"
              class="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {{ errorMessage }}
            </div>

            <!-- Sign In Button -->
            <div class="pt-4">
              <button
                type="submit"
                :disabled="
                  isLoading ||
                  isRateLimited ||
                  !email.trim() ||
                  !password.trim() ||
                  !!emailError ||
                  !!passwordError
                "
                class="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg transition-all duration-200 shadow-lg text-white bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <span v-if="isRateLimited"> Try again in {{ remainingDelay }}s. </span>
                <span v-else-if="isLoading" class="flex items-center">
                  <svg
                    class="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing In...
                </span>
                <span v-else class="flex items-center">
                  Sign In
                  <svg
                    class="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Trusted Device Popup for Admins -->
    <div
      v-if="showTrustDevice"
      class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
    >
      <div
        class="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4 border-2 border-green-500 animate-pulse"
      >
        <!-- Success Icon -->
        <div class="text-center mb-4">
          <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3"
          >
            <svg
              class="h-6 w-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-gray-900 mb-2">Trust This Device?</h3>
          <p class="text-sm text-gray-600">Allow faster future admin logins</p>
        </div>

        <!-- Device Name Input -->
        <div class="mb-4">
          <label for="device-name" class="block text-xs font-medium text-gray-700 mb-2">
            Device Name (Optional)
          </label>
          <input
            v-model="deviceName"
            type="text"
            id="device-name"
            :placeholder="trustedDeviceService.getDeviceInfo().name"
            class="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          />
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-2">
          <button
            @click="skipTrustDevice"
            class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Skip
          </button>
          <button
            @click="addTrustedDevice"
            class="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Trust Device
          </button>
        </div>
      </div>
    </div>

    <!-- Email Confirmation Modal -->
    <div
      v-if="showEmailConfirmation"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-xl shadow-xl p-8 max-w-md w-full mx-4 border-2 border-red-500">
        <!-- Email Icon -->
        <div class="text-center mb-6">
          <div
            class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4"
          >
            <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-2">Email Confirmation Required</h3>
          <p class="text-gray-600">We've sent a confirmation link to your email address</p>
        </div>

        <!-- Email Info -->
        <div class="bg-gray-50 rounded-lg p-4 mb-6">
          <p class="text-sm text-gray-700">
            <strong>Email sent to:</strong> {{ pendingAdminAccess?.email }}
          </p>
          <p class="text-sm text-gray-600 mt-2">
            Please check your email and click the confirmation link to continue accessing the admin
            dashboard.
          </p>
        </div>

        <!-- Status -->
        <div class="mb-6">
          <div class="flex items-center justify-center">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mr-3"></div>
            <span class="text-sm text-gray-600">Waiting for email confirmation...</span>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-3">
          <button
            @click="handleEmailConfirmationTimeout"
            class="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="
              pendingAdminAccess && checkEmailConfirmationStatus(pendingAdminAccess.confirmationId)
            "
            :disabled="!pendingAdminAccess"
            class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Check Status
          </button>
        </div>

        <!-- Info Text -->
        <p class="text-xs text-gray-500 mt-4 text-center">
          This confirmation is required for security when logging in from an untrusted device.
        </p>
      </div>
    </div>

    <!-- Loading Popup -->
    <div
      v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
    >
      <div
        class="bg-white rounded-xl shadow-xl p-6 max-w-xs w-full mx-4 border border-blue-200 animate-pulse pointer-events-auto"
      >
        <!-- Loading Animation -->
        <div class="text-center">
          <div
            class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-50 mb-3"
          >
            <svg
              class="animate-spin h-6 w-6 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-1">Signing You In</h3>

          <!-- Progress Dots -->
          <div class="flex justify-center space-x-1 mt-3">
            <div class="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"></div>
            <div
              class="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"
              style="animation-delay: 0.1s"
            ></div>
            <div
              class="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce"
              style="animation-delay: 0.2s"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-bg {
  background-image: url('/login.webp');
}

/* Fallback for browsers that don't support WebP */
.no-webp .login-bg {
  background-image: url('/login.png');
}

.login-container {
  box-shadow: 8px 8px 0px #000;
  max-width: 800px;
  margin: 0 auto;
}
</style>
